import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type { EntityDetail, ReviewDTO } from "../../shared/api";
import type { EntityType } from "../../shared/dimensions";
import { Stars } from "../components/Stars";
import { ContractSafety } from "../components/ContractSafety";
import { Logo, Spinner, Empty, ErrorState } from "../components/States";
import { ClaimBadge, AuditBadge } from "../components/Badge";
import { Radar } from "../components/Radar";
import { ReviewCard } from "../components/ReviewCard";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { useRouteData, entityKey, reviewsKey } from "../lib/routeData";
import { origin } from "../lib/origin";
import { fmtUsd, fmtRating, shortAddr, utcMinute, yearsSince } from "../lib/format";

export function Profile({ type }: { type: EntityType }) {
  const { slug } = useParams();
  const { data: e, error, loading } = useRouteData<EntityDetail>(
    entityKey(type, slug ?? ""),
    () => api.entity(type, slug!),
  );

  // The first page of reviews is seeded by the server: it carries the Review
  // structured data, so it has to be in the initial HTML.
  // Depends on the entity, so it must not run until the entity exists. When the
  // server seed is absent — every SPA-fallback render — `e` is null on the first
  // pass, and reading e.id there threw and blanked the page.
  const seededReviews = useRouteData<{ items: ReviewDTO[] }>(
    reviewsKey(type, slug ?? ""),
    () => api.reviews(e!.id, "verified"),
    { enabled: !!e },
  );

  const [sort, setSort] = useState("verified");
  const [sorted, setSorted] = useState<ReviewDTO[] | null>(null);
  const reviews = sort === "verified" ? (sorted ?? seededReviews.data?.items ?? null) : sorted;

  useEffect(() => {
    if (!e) return;
    // The default sort is already seeded; only fetch when the reader changes it.
    if (sort === "verified" && seededReviews.data?.items) { setSorted(null); return; }
    setSorted(null);
    api.reviews(e.id, sort).then((r) => setSorted(r.items)).catch(() => setSorted([]));
  }, [e?.id, sort]);

  useMeta(
    e
      ? metaFor({ route: "entity", base: origin(), entity: e, reviews: reviews ?? [] })
      : null,
  );

  if (error) return <ErrorState message={error} />;
  if (loading || !e) return <Spinner />;

  const bd = e.ratingBreakdown;

  return (
    <div>
      {/* Header */}
      <div className="profile-header">
        <Logo url={e.logoUrl} name={e.name} size={64} />
        <div className="u-9405df2">
          <div className="row wrap u-f3d7ce7">
            <h1 className="m-0">{e.name}</h1>
            {e.symbol && <span className="mono muted u-951b72d">{e.symbol}</span>}
            <ClaimBadge status={e.claimStatus} />
            {type === "token" && <AuditBadge status={e.signals.auditStatus} />}
          </div>
          <p className="u-6157bc4">{e.shortDesc}</p>
          <div className="row wrap u-d87ce2e">
            {e.categories.map((c) => (
              <Link key={c.slug} to={`/category/${c.slug}`} className="badge badge-neutral">{c.name}</Link>
            ))}
          </div>
        </div>
        <Link to={`/review/new/${e.id}`} className="btn btn-primary">Write a review</Link>
      </div>

      {/* Score band. With no reviews there is nothing to state, and four
          em-dash tiles read as a broken page rather than as an empty one, so
          say the honest thing in one line instead. */}
      {e.reviewCount > 0 ? (
        <div className="score-band u-49fcd85">
          <div className="score-cell">
            <div className="label">Weighted rating</div>
            <div className="row gap-8">
              <span className="big rating-num">{fmtRating(e.ratingWeighted)}</span>
              <Stars value={e.ratingWeighted} />
            </div>
            <div className="sub">{e.reviewCount} review{e.reviewCount === 1 ? "" : "s"}</div>
          </div>
          <div className="score-cell">
            <div className="label">Raw average</div>
            <div className="big rating-num">{fmtRating(e.ratingRaw)}</div>
            <div className="sub">unweighted mean</div>
          </div>
          <div className="score-cell">
            <div className="label">Trust score</div>
            <div className="big rating-num">{e.trustScore != null ? Math.round(e.trustScore) : "—"}</div>
            <div className="sub">
              {e.trustScore != null ? (
                <Link to="/methodology" className="muted">how it&rsquo;s computed →</Link>
              ) : (
                // Never show a confident number built on too little (SPEC §2.5).
                <Link to="/methodology" className="muted">not enough data to score yet →</Link>
              )}
            </div>
          </div>
          <div className="score-cell">
            <div className="label">Verified vs unverified</div>
            <div className="big rating-num">
              <span className="badge badge-verified">{bd.verified.count}</span>{" "}
              <span className="badge badge-neutral">{bd.unverified.count}</span>
            </div>
            <div className="sub">
              {bd.verified.avg != null ? `${fmtRating(bd.verified.avg)}★ verified` : "no verified yet"}
            </div>
          </div>
        </div>
      ) : (
        <div className="no-score-band">
          <div>
            <b>No reviews yet.</b>{" "}
            <span className="muted">
              There is no rating or Trust Score until at least three people have reviewed this —
              a score built on one opinion would be noise.{" "}
              <Link to="/methodology">How scoring works →</Link>
            </span>
          </div>
          <Link to={`/review/new/${e.id}`} className="btn btn-primary btn-sm">Be the first</Link>
        </div>
      )}

      {/* Contract safety — the most decision-relevant block on a token page,
          and the one thing here that no affiliate listicle can fake. */}
      {type === "token" && (
        <ContractSafety
          chain={e.signals.chain}
          contractAddress={e.signals.contractAddress}
          deployerAddress={e.signals.deployerAddress}
          securityFlags={e.signals.securityFlags}
          securityScannedAt={e.signals.securityScannedAt}
        />
      )}

      {/* Objective signals */}
      <section className="section">
        <h2>Objective signals</h2>
        <div className="signal-grid">
          {/* Only render a tile that has a value. A grid of em-dashes reads as
              a broken page, and says nothing a missing tile would not. */}
          {type === "token" ? (
            <>
              {e.signals.chain && <Signal k="Chain" v={e.signals.chain} />}
              {e.signals.contractDeployedAt && (
                <Signal k="Contract age" v={yearsSince(e.signals.contractDeployedAt)} />
              )}
              {e.signals.market?.priceUsd != null && <Signal k="Price" v={fmtUsd(e.signals.market.priceUsd)} />}
              {e.signals.market?.marketCapUsd != null && (
                <Signal k="Market cap" v={fmtUsd(e.signals.market.marketCapUsd)} />
              )}
              {e.signals.market?.volume24hUsd != null && (
                <Signal k="24h volume" v={fmtUsd(e.signals.market.volume24hUsd)} />
              )}
            </>
          ) : (
            <>
              {e.signals.companyType && <Signal k="Type" v={e.signals.companyType} />}
              {e.signals.foundedYear != null && <Signal k="Founded" v={e.signals.foundedYear.toString()} />}
              {e.signals.hqCountry && <Signal k="HQ" v={e.signals.hqCountry} />}
              {e.signals.supportsFiat != null && (
                <Signal k="Fiat support" v={e.signals.supportsFiat ? "Yes" : "No"} />
              )}
            </>
          )}
        </div>
        {type === "token" && e.signals.market?.asOf && (
          <p className="muted u-dd17410">
            Market data as of {utcMinute(e.signals.market.asOf)} · cached, for reference only.
          </p>
        )}
      </section>

      {/* Dimensions + relationships */}
      <section className="section u-e249446">
        {e.dimensionAverages.some((d) => d.count > 0) && (
          <div>
            <h2>Rating breakdown</h2>
            <div className="u-2149cc9">
              <Radar data={e.dimensionAverages.filter((d) => d.count > 0).map((d) => ({ label: d.label, avg: d.avg }))} />
            </div>
          </div>
        )}
        <div>
          <h2>Relationships</h2>
          {e.links.length === 0 ? (
            <Empty title="No linked entities" />
          ) : (
            <div className="grid gap-8">
              {e.links.map((l, i) => (
                <Link key={i} to={`/${l.entity.type}/${l.entity.slug}`} className="card u-3c74cff">
                  <span>{l.entity.name}</span>
                  <span className="muted u-474a305">{l.relation.replace(/_/g, " ")}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Reviews */}
      <section className="section">
        <div className="row between">
          <h2 className="m-0">Reviews</h2>
          <div className="pill-group">
            {([["verified", "Verified first"], ["helpful", "Most helpful"], ["newest", "Newest"]] as const).map(([k, lbl]) => (
              <button key={k} className={sort === k ? "active" : ""} onClick={() => setSort(k)}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="grid u-a70bff7">
          {!reviews ? (
            <Spinner />
          ) : reviews.length === 0 ? (
            <Empty title="No reviews yet" hint="Be the first to share verified, honest feedback." />
          ) : (
            reviews.map((r) => <ReviewCard key={r.id} review={r} entityType={type} />)
          )}
        </div>
      </section>

      <div className="nfa u-583f758">
        <strong>Not financial advice.</strong> This page shows user opinions and verifiable facts, not a
        recommendation to buy, sell, or hold any asset.
      </div>
    </div>
  );
}

function Signal({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="signal">
      <div className="k">{k}</div>
      <div className={`v metric ${mono ? "mono" : ""}`}>{v}</div>
    </div>
  );
}
